import React, { useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { scrapeJob } from '../services/jobScraper'

const SCORE_COLOR = { A: '#7fc97f', B: '#58a6ff', C: '#e8c47f', D: '#e8a07f', F: '#ff8888' }
const SCORE_LABEL = { A: 'Strong Apply', B: 'Apply', C: 'Consider', D: 'Stretch', F: 'Skip' }

export function EvaluationView({ job, profile, onBack, onSaveStories, onTailor }) {
  const { token } = useAuth()
  const [jobDesc, setJobDesc] = useState(job.job_description || '')
  const [fetching, setFetching] = useState(false)
  const [evaluating, setEvaluating] = useState(false)
  const [result, setResult] = useState(job.evaluation_json || null)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  const fetchDesc = async () => {
    if (!job.job_url) return
    setFetching(true)
    setError(null)
    try {
      const scraped = await scrapeJob(job.job_url)
      setJobDesc(scraped.description || '')
    } catch (e) {
      setError('Could not fetch job description. Paste it manually.')
    } finally {
      setFetching(false)
    }
  }

  const evaluate = async () => {
    if (!jobDesc.trim()) { setError('Job description required.'); return }
    if (!profile?.name) { setError('Complete your profile first.'); return }
    setEvaluating(true)
    setError(null)
    try {
      const res = await fetch('/api/ark/evaluate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          profile,
          jobDescription: jobDesc,
          jobTitle: job.job_title,
          company: job.company_name,
        }),
      })
      if (!res.ok) throw new Error((await res.json()).error || 'Evaluation failed')
      const data = await res.json()
      setResult(data)

      // Persist evaluation to pipeline entry if we have an ID
      if (token && job.pipeline_id) {
        await fetch(`/api/ark/pipeline/${job.pipeline_id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            score: data.score,
            score_letter: data.score_letter,
            evaluation_json: data,
            job_description: jobDesc,
            status: 'evaluating',
          }),
        })
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setEvaluating(false)
    }
  }

  const saveStories = async () => {
    if (!token || !result?.star_stories?.length) return
    for (const s of result.star_stories) {
      await fetch('/api/ark/stories', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: s.title,
          situation: s.situation,
          task: s.task,
          action: s.action,
          result: s.result,
          reflection: s.reflection,
          competency_tags: s.competency_tags || [],
          source_job_id: job.pipeline_id || null,
        }),
      })
    }
    setSaved(true)
    if (onSaveStories) onSaveStories(result.star_stories)
  }

  return (
    <div className="ark-eval-view">
      <div className="ark-eval-header">
        <button className="ark-back-btn" onClick={onBack}>← Back</button>
        <div>
          <div className="ark-eval-job-title">{job.job_title}</div>
          <div className="ark-eval-company">{job.company_name}</div>
        </div>
        {result && (
          <div className="ark-eval-score-badge" style={{ background: SCORE_COLOR[result.score_letter] }}>
            <span className="ark-eval-score-letter">{result.score_letter}</span>
            <span className="ark-eval-score-label">{SCORE_LABEL[result.score_letter]}</span>
          </div>
        )}
      </div>

      {/* Job description input */}
      {!result && (
        <div className="ark-eval-input-section">
          <div className="ark-eval-input-header">
            <label className="ark-eval-label">Job Description</label>
            {job.job_url && (
              <button className="ark-eval-fetch-btn" onClick={fetchDesc} disabled={fetching}>
                {fetching ? 'Fetching…' : 'Auto-fetch →'}
              </button>
            )}
          </div>
          <textarea
            className="ark-eval-textarea"
            placeholder="Paste the job description here, or use auto-fetch…"
            value={jobDesc}
            onChange={e => setJobDesc(e.target.value)}
            rows={10}
          />
          {error && <div className="ark-eval-error">{error}</div>}
          <button className="ark-eval-run-btn" onClick={evaluate} disabled={evaluating || !jobDesc.trim()}>
            {evaluating ? 'Evaluating…' : 'Evaluate Fit →'}
          </button>
        </div>
      )}

      {/* Evaluation result */}
      {result && (
        <div className="ark-eval-result">

          <div className="ark-eval-block">
            <h3 className="ark-eval-block-title">Role Summary</h3>
            <p className="ark-eval-block-body">{result.role_summary}</p>
          </div>

          <div className="ark-eval-block">
            <h3 className="ark-eval-block-title">
              CV Match — <span style={{ color: '#58a6ff' }}>{result.cv_match?.match_percent}%</span>
            </h3>
            <div className="ark-eval-match-lists">
              <div>
                <div className="ark-eval-sublabel">Strengths</div>
                <ul className="ark-eval-list">
                  {(result.cv_match?.strengths || []).map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
              <div>
                <div className="ark-eval-sublabel">Gaps</div>
                <ul className="ark-eval-list ark-eval-list--gaps">
                  {(result.cv_match?.gaps || []).map((g, i) => <li key={i}>{g}</li>)}
                </ul>
              </div>
            </div>
          </div>

          <div className="ark-eval-block">
            <h3 className="ark-eval-block-title">Comp Research</h3>
            <p className="ark-eval-block-body">{result.comp_research}</p>
          </div>

          <div className="ark-eval-block">
            <h3 className="ark-eval-block-title">Why You Fit</h3>
            <p className="ark-eval-block-body">{result.personalization}</p>
          </div>

          <div className="ark-eval-block">
            <h3 className="ark-eval-block-title">Interview Prep</h3>
            {(result.interview_prep || []).map((q, i) => (
              <div key={i} className="ark-eval-interview-item">
                <div className="ark-eval-question">Q: {q.question}</div>
                <div className="ark-eval-story-hint">→ {q.story_hint}</div>
              </div>
            ))}
          </div>

          <div className="ark-eval-block ark-eval-block--rec">
            <h3 className="ark-eval-block-title">Recommendation</h3>
            <p className="ark-eval-block-body" style={{ fontWeight: 600 }}>{result.recommendation}</p>
          </div>

          {result.star_stories?.length > 0 && (
            <div className="ark-eval-block">
              <div className="ark-eval-stories-header">
                <h3 className="ark-eval-block-title">STAR Stories ({result.star_stories.length})</h3>
                {token && !saved && (
                  <button className="ark-eval-save-stories-btn" onClick={saveStories}>
                    Save to Story Bank
                  </button>
                )}
                {saved && <span className="ark-eval-saved-label">✓ Saved</span>}
              </div>
              {result.star_stories.map((s, i) => (
                <div key={i} className="ark-eval-story">
                  <div className="ark-eval-story-title">{s.title}</div>
                  <div className="ark-eval-story-tags">
                    {(s.competency_tags || []).map(t => (
                      <span key={t} className="ark-eval-tag">{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="ark-eval-actions">
            <button className="ark-eval-run-btn" onClick={() => setResult(null)}>Re-evaluate</button>
            <button className="ark-queue-tailor-btn" onClick={() => onTailor({ ...job, job_description: jobDesc, evaluation_json: result })}>
              Generate Resume →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
